import React, { useState, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Swal from "sweetalert2";
import "./ResolveInstallation.css";
import { Box, IconButton, Typography, Tabs, Tab } from "@mui/material";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";

import GlobalButton from "../../shared/utils/GlobalButton";
import GlobalChip from "../../shared/utils/GlobalChip";
import axios from "axios";
import Decrypt from "../../shared/utils/Decrypt";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { useMultiMediaUpload } from "../../helperFunctions/Usemultimediaupload.js";
import { CreateGeotaggedImage } from "../../helperFunctions/createGeoTagImage.js";
import MediaPreview from "../../helperFunctions/MediaPreview.js";

const ResolveInstallation = ({
  data,
  onClose,
  loading,
  onFlagResolve,
  setLoading,
  vendorView = false,
}) => {
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [loadingDownload, setLoadingDownload] = useState(false);
  const serverURL = process.env.REACT_APP_URL;
  const resolvedBy = localStorage.getItem("userId");
  const reportRef = useRef(null);
  const evidenceRef = useRef(null);
  const FlaggedStatus = data?.status || "Flagged";
  const [mediaTab, setMediaTab] = useState("photos");
  const [mediaPreview, setMediaPreview] = useState(null);
  const { locationDetails } = data || {};

  const media = useMultiMediaUpload({
    mode: "single",
    allowedTypes: ["image/jpeg", "image/png"],
    maxSizeMB: 2,
  });

  const uploadedItem = media.items[0];

  const lat =
    locationDetails?.locationCoordinates?.latitude ||
    locationDetails?.locationCoordinates?.lat;

  const lng =
    locationDetails?.locationCoordinates?.longitude ||
    locationDetails?.locationCoordinates?.lng;

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];

    e.target.value = "";

    if (!file) return;

    try {
      media.setError("");

      const geotaggedFile = await CreateGeotaggedImage({
        file,
        latitude: lat,
        longitude: lng,
        address: locationDetails?.address,
        city: locationDetails?.city,
        state: locationDetails?.state,
        zipCode: locationDetails?.zipCode,
        googleMapKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
      });

      media.addFiles([geotaggedFile]);
    } catch (error) {
      console.error("Geotag image generation error:", error);

      media.setError("Unable to generate geotagged image. Please try again.");
    }
  };

  const fetchImageAsBase64 = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl, { mode: "cors" });
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error(`❌ Error fetching image: ${imageUrl}`, error);
      return null;
    }
  };

  const replaceImageSrcWithBase64 = async () => {
    const imgElements = document.querySelectorAll("img");
    for (let img of imgElements) {
      const src = img.getAttribute("src");
      if (src && !src.startsWith("data:image")) {
        const base64 = await fetchImageAsBase64(src);
        if (base64) img.src = base64;
      }
    }
  };

  const forceBrowserRepaint = (element) => {
    element.style.display = "none";
    void element.offsetHeight;
    element.style.display = "";
  };

  const waitForImagesToLoad = async (element) => {
    const images = element.querySelectorAll("img");
    const promises = [];
    images.forEach((img) => {
      if (!img.complete || img.naturalHeight === 0) {
        promises.push(
          new Promise((res) => {
            img.onload = res;
            img.onerror = res;
          }),
        );
      }
    });
    return Promise.all(promises);
  };

  const waitAndRepaint = async () => {
    await new Promise((res) => setTimeout(res, 300));
    void document.body.offsetHeight;
  };

  const handleDownloadPdf = async () => {
    const element = reportRef.current;
    if (!element) return;

    const evidenceElement = evidenceRef.current;
    let placeholder = null;

    try {
      if (evidenceElement) {
        placeholder = document.createComment("evidence-placeholder");

        evidenceElement.parentNode.insertBefore(placeholder, evidenceElement);

        evidenceElement.remove();
      }

      setLoadingDownload(true);

      await replaceImageSrcWithBase64();
      forceBrowserRepaint(element);
      await waitForImagesToLoad(element);
      await waitAndRepaint();

      const dataUrl = await toPng(element, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: "#FFFFFF",
        cacheBust: true,
        useCORS: true,
        width: element.scrollWidth,
        height: element.scrollHeight,
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4",
        compress: true,
      });

      const marginX = 0;
      const marginY = 0;
      const pageWidth = pdf.internal.pageSize.getWidth() - 2 * marginX;
      const pageHeight = pdf.internal.pageSize.getHeight() - 2 * marginY;

      const img = new Image();
      img.src = dataUrl;

      await new Promise((resolve) => {
        img.onload = () => {
          const imgWidth = pageWidth;
          const imgHeight = (img.height * imgWidth) / img.width;

          if (imgHeight <= pageHeight) {
            pdf.addImage(dataUrl, "PNG", marginX, marginY, imgWidth, imgHeight);
          } else {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);

            const pxPerPage = (pageHeight * img.width) / pageWidth;
            let renderedPx = 0;
            let pageIndex = 0;

            while (renderedPx < img.height) {
              const sliceHeight = Math.min(pxPerPage, img.height - renderedPx);

              const pageCanvas = document.createElement("canvas");
              pageCanvas.width = img.width;
              pageCanvas.height = sliceHeight;
              const pageCtx = pageCanvas.getContext("2d");
              pageCtx.drawImage(
                canvas,
                0,
                renderedPx,
                img.width,
                sliceHeight,
                0,
                0,
                img.width,
                sliceHeight,
              );

              const pageData = pageCanvas.toDataURL("image/png");
              const pageImgHeight = (sliceHeight * pageWidth) / img.width;

              if (pageIndex > 0) pdf.addPage();
              pdf.addImage(
                pageData,
                "PNG",
                marginX,
                marginY,
                pageWidth,
                pageImgHeight,
              );

              renderedPx += sliceHeight;
              pageIndex += 1;
            }
          }

          pdf.save(`Installation_Flag_${data?.ticketNumber || "report"}.pdf`);
          resolve();
        };
        img.onerror = resolve;
      });
    } catch (error) {
      console.error("❌ Error capturing PDF:", error);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: "Could not generate the PDF. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      if (evidenceElement && placeholder?.parentNode) {
        placeholder.parentNode.insertBefore(evidenceElement, placeholder);

        placeholder.remove();
      }

      setLoadingDownload(false);
    }
  };

  const handleResolveFlag = async () => {
    if (!uploadedItem?.file) {
      setError("Please Select a Installation Image");
      return;
    }

    if (uploadedItem?.uploading) {
      setError("Please wait for the image to finish uploading.");
      return;
    }

    if (!uploadedItem?.url) {
      setError("Image upload failed. Please re-select the file.");
      return;
    }

    if (!description) {
      setError("Please enter a description");
      return;
    }
    try {
      setError("");
      setLoading(true);
      const payload = {
        resolvedRemarks: description,
        resolvedInstallationImage: uploadedItem.url,
        resolvedBy: resolvedBy,
      };

      const response = await axios.post(
        `${serverURL}/installation-flags/product/${data?.productId}/resolve`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      console.log(response.data);

      const status = response?.data?.data?.status;

      // Parent ko updated status bhejo
      onFlagResolve?.(status);

      media?.reset();

      // Modal close
      onClose();

      Swal.fire({
        icon: "success",
        title: "Installation Resolved",
        text: response?.data?.message,
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Resolve installation error:", error);

      setError(
        error?.response?.data?.message ||
          "Something went wrong while resolving installation",
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const displayError = error || media?.error;

  return (
    <>
      <div ref={reportRef} style={{ background: "#FFFFFF" }}>
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            borderRadius: "8px",
            padding: "24px",
            gap: "24px",
          }}
        >
          {/* Heading row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <h2 className="font-bold text-primary">
              {vendorView
                ? "Flag Details"
                : FlaggedStatus === "Flagged"
                  ? "Resolve Installation"
                  : "Details"}
            </h2>

            <IconButton className="pdf-hide" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              border: "1px solid #d9d9d9",
              borderRadius: "7px",
              p: 2,
              mb: 1,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: 216,
                  height: 166,
                  flexShrink: 0,
                  borderRadius: "4px",
                  overflow: "hidden",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <img
                  src={data?.installationImage}
                  alt="Installation"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Box
                sx={{
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    width: "550px",
                  }}
                >
                  <GlobalChip label={`# ${data?.ticketNumber}`} />

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    <GlobalChip label={data?.flagIssue} type="warning" />
                    <img
                      src={
                        FlaggedStatus === "Flagged"
                          ? "/assests/AdminDashboard/flag_filled.png "
                          : "/assests/AdminDashboard/flag_green.png"
                      }
                      width={30}
                      height={30}
                      alt="flag"
                      style={{ display: "block" }}
                    />
                  </div>
                </div>
                <h4 className="font-semibold text-primary">
                  {data?.productType || ""}
                </h4>

                <div className="flex items-center">
                  <p className="text-tertiary font-small">Vendor :&nbsp; </p>
                  <h6 className="text-anchor font-medium">
                    {data?.vendorBusinessName || ""}
                  </h6>
                </div>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <img
                    src="/assests/AdminDashboard/campaign.png"
                    alt="campaign"
                    style={{
                      width: "14px",
                      height: "14px",
                      objectFit: "contain",
                      flexShrink: 0,
                    }}
                  />

                  <p className="font-medium text-primary">
                    {data?.campaignTitle}
                  </p>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <img
                    src="/assests/AdminDashboard/store.png"
                    alt="store"
                    style={{
                      width: "14px",
                      height: "14px",
                      objectFit: "contain",
                      flexShrink: 0,
                    }}
                  />

                  <p font-medium text-primary>
                    {data?.storeName}
                  </p>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* --- evidence media: Photos / Video tabs --- */}
          <Box ref={evidenceRef}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#253041",
                mb: 0.7,
              }}
            >
              EVIDENCE
            </Typography>

            <Box
              sx={{ border: "1px solid #d9d9d9", borderRadius: "5px", mb: 1.8 }}
            >
              <Tabs
                value={mediaTab}
                onChange={(_, val) => setMediaTab(val)}
                sx={{
                  minHeight: 38,
                  borderBottom: "1px solid #eee",
                  "& .MuiTab-root": {
                    minHeight: 38,
                    fontSize: "12.5px",
                    fontWeight: 500,
                    textTransform: "none",
                  },
                }}
              >
                <Tab
                  value="photos"
                  label={`Photos (${data?.evidenceImages.length})`}
                />
                <Tab
                  value="video"
                  label={`Video (${data?.evidenceVideo ? 1 : 0})`}
                />
              </Tabs>

              <Box sx={{ p: 1.2 }}>
                {mediaTab === "photos" && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: 1,
                    }}
                  >
                    {data?.evidenceImages.map((item, index) => (
                      <Box
                        key={index}
                        onClick={() =>
                          setMediaPreview({
                            type: "image",
                            src: item,
                          })
                        }
                        sx={{
                          position: "relative",
                          height: "100px",
                          borderRadius: "6px",
                          overflow: "hidden",
                          backgroundColor: "#f5f5f5",
                          cursor: "pointer",
                        }}
                      >
                        <img
                          src={item}
                          alt={`Evidence ${index + 1}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}

                {mediaTab === "video" && (
                  <Box>
                    {data?.evidenceVideo ? (
                      <Box
                        onClick={() =>
                          setMediaPreview({
                            type: "video",
                            src: data?.evidenceVideo,
                          })
                        }
                        sx={{
                          position: "relative",
                          width: 120,
                          height: "100px",
                          borderRadius: "6px",
                          overflow: "hidden",
                          backgroundColor: "#000",
                          cursor: "pointer",
                        }}
                      >
                        <video
                          src={data?.evidenceVideo}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <PlayCircleIcon
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            color: "#fff",
                            fontSize: 26,
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          position: "relative",
                          width: 120,
                          height: 72,
                          borderRadius: "6px",
                          overflow: "hidden",
                          backgroundColor: "#000",
                        }}
                      >
                        <Typography sx={{ fontSize: "10px" }}>
                          No Video Found
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Timeline */}
          <div className="timeline" style={{ paddingLeft: 16 }}>
            <div className="timeline-item red">
              <div className="flex justify-between items-center mb-2">
                <h6 className="font-semibold text-primary">
                  Installation Flagged
                </h6>
                <p className="text-tertiary font-small mb-2">
                  {formatDateTime(data?.activityLog?.[0]?.at)}
                </p>
              </div>

              <p className="text-tertiary mb-2">
                Flagged by {data?.flaggedBy?.name}
              </p>
              {data?.description && (
                <div
                  style={{
                    padding: "8px",
                    background: "#ffffff",
                    borderRadius: "2px",
                  }}
                >
                  <p className="font-medium text-secondary">
                    {data?.description}
                  </p>
                </div>
              )}
            </div>

            <div className="timeline-item blue ">
              <div className="flex justify-between items-center mb-2">
                <h6 className="font-semibold text-primary mb-2">
                  Vendor Notified via System
                </h6>
                <p className="text-tertiary font-small">
                  {formatDateTime(data?.activityLog?.[1]?.at)}
                </p>
              </div>

              <p className="text-tertiary">
                Automated WhatsApp to {Decrypt(data?.vendorName)} ({" "}
                {Decrypt(data?.vendorPhoneNo)} ).
              </p>
            </div>

            {FlaggedStatus === "Flagged" && !vendorView ? (
              <div className="timeline-item green resolve-form">
                <h6 className="font-semibold text-primary">
                  Resolve Installation
                </h6>

                <label className="upload-box">
                  {uploadedItem?.preview ? (
                    <div className="upload-preview">
                      <img src={uploadedItem.preview} alt="Preview" />

                      <button
                        type="button"
                        className="remove-image"
                        onClick={() => {
                          media.reset();
                          setError("");
                        }}
                      >
                        ×
                      </button>

                      {uploadedItem?.uploading && (
                        <div className="upload-loader">Uploading...</div>
                      )}
                    </div>
                  ) : (
                    <label className="upload-box">
                      <div className="upload-icon">
                        <UploadFileIcon />
                      </div>

                      <h6 className="font-semibold text-primary">
                        Click to upload
                      </h6>

                      <p className="text-secondary font-small">
                        JPEG/PNG, Max. 1MB
                      </p>

                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/png"
                        onChange={handleImageUpload}
                        disabled={uploadedItem?.uploading}
                      />
                    </label>
                  )}
                </label>
                <div style={{ gap: "8px" }}>
                  <h6 className="font-semibold text-primary">Description *</h6>
                  <textarea
                    className="text-primary mt-2"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Vendor shared updated installation images. Installation verified and concern accepted."
                  />{" "}
                  {displayError && <p className="text-error">{displayError}</p>}
                </div>
              </div>
            ) : FlaggedStatus === "Resolved" ? (
              <div className="timeline-item green resolve-form">
                <div className="flex justify-between items-center mb-2">
                  <h6 className="font-semibold text-primary">Issue Resolved</h6>
                  <p className="text-tertiary font-small">
                    {formatDateTime(data?.activityLog?.[2]?.at)}
                  </p>
                </div>

                <p className="text-tertiary">
                  {data?.resolvedBy?.name} submitted 1 rectified photograph with
                  explanatory notes.
                </p>
                <Box
                  sx={{
                    width: 216,
                    height: 166,
                    flexShrink: 0,
                    borderRadius: "4px",
                    overflow: "hidden",
                    backgroundColor: "#f5f5f5",
                    marginY: "12px",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setMediaPreview({
                      type: "image",
                      src: data?.resolvedInstallationImage,
                    })
                  }
                >
                  <img
                    src={data?.resolvedInstallationImage}
                    alt="Installation"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <div
                  style={{
                    padding: "8px",
                    background: "#ffffff",
                    borderRadius: "2px",
                  }}
                >
                  <p className="font-medium text-secondary">
                    {data?.resolvedRemarks}
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </Box>
      </div>
      <div className="p-[24px]">
        {!vendorView &&
          (FlaggedStatus === "Flagged" ? (
            <GlobalButton
              size="large"
              onClick={handleResolveFlag}
              disabled={loading}
            >
              {loading ? "Resolving..." : "Resolve Installation"}
            </GlobalButton>
          ) : (
            <GlobalButton
              size="large"
              onClick={handleDownloadPdf}
              disabled={loadingDownload}
            >
              {loadingDownload ? "Downloading..." : "Download PDF"}
            </GlobalButton>
          ))}
      </div>
      <MediaPreview
        open={!!mediaPreview}
        onClose={() => setMediaPreview(null)}
        type={mediaPreview?.type}
        src={mediaPreview?.src}
      />
    </>
  );
};

export default ResolveInstallation;
